/**
 * EventWeatherBadge — async, fail-silent weather pill for event surfaces.
 *
 * Usage:
 *   <EventWeatherBadge eventId={event.id} />
 *
 * Renders nothing until the fetch resolves. If the API returns null (no key,
 * beyond horizon, geo missing, network error) the component stays invisible
 * — the spec is explicit: never block event rendering, never show an error.
 */
import { useEffect, useState } from "react";
import { CloudRain, Sun } from "lucide-react";

import { api } from "../../lib/chalkApi";

export default function EventWeatherBadge({ eventId }) {
  const [wx, setWx] = useState(null);

  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const d = await api.eventWeather(eventId);
        if (!cancelled && d && d.weather) setWx(d.weather);
      } catch {
        /* silent — spec requires fail-silent on weather */
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  if (!wx) return null;
  const rainy = wx.rain_pct >= 40;
  return (
    <span
      data-testid={`event-weather-${eventId}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "3px 8px", borderRadius: 999,
        background: rainy ? "rgba(31,92,184,0.10)" : "rgba(255,184,28,0.14)",
        color: rainy ? "#1F5CB8" : "#7A5A00",
        fontSize: 11, fontWeight: 700, letterSpacing: "0.03em",
        whiteSpace: "nowrap",
      }}
      title={`${wx.condition} · ${wx.rain_pct}% rain`}
    >
      {rainy ? <CloudRain size={11} aria-hidden="true" /> : <Sun size={11} aria-hidden="true" />}
      {wx.temp_f}° · {wx.rain_pct}%
    </span>
  );
}
